/**
 * Catalogue create flows — new facts and variants, written from the
 * hierarchy tree's inline draft rows.
 *
 * 2026-06-11 (Ed): the legacy "+ Add Custom" form is retired with the rest
 * of the pre-Trackable surface. Creation now lives where curation lives:
 * the category ⋯ menu ("Add fact…") and the per-parent + (add variant).
 *
 * Goal Category and Goal Sub-category are dropdowns sourced from
 * `_goalCategory` / `_goalSubcategory`. Each dropdown also offers a
 * `+ Add new …` option that opens an inline create flow, writes the
 * new hierarchy doc, refreshes the dropdown, and selects the new
 * entry. Goal name remains free-text — the librarian is authoring the
 * leaf.
 *
 * Net effect: the catalogue can grow only through controlled paths.
 * Free-text Goal Sub-category drift (e.g. `Domestic & Household
 * Chores` vs `Domestic / Household Chores`, the bug class that
 * motivated OMON-079) becomes structurally impossible.
 *
 * OMON-079 79h Half 2 (2026-05-10) — replaces the prior screenshot-
 * state form which had a freetext Goal Sub-category input.
 */

import { collection, doc, writeBatch } from "firebase/firestore";

import { state } from "../state";
import { db } from "../firebase";
import { statusEl } from "../dom";
import type { ObservationDefinition, ActivityDefinition } from "../types";
import { trackableCapabilities } from "../types";
import { resolveCategoryCodes } from "./category";
import { LEVEL_NAMES } from "../main";
// OMON-143 (2026-05-24): stamp meta.security on custom-goal writes from
// `dataScope: test` librarians. Hierarchy.ts (OMON-111) was the pattern;
// this is the long-tail roll-out for `_observationDefinition` +
// `_activityDefinition` create paths.
import { metaSecurityFor } from "../admin/meta-security";

/**
 * OMON-192: mint an opaque, collision-proof id for a NEW catalogue fact, so
 * identity is meaningless and never a fossil of the title. The title stays the
 * human label; `code.coding.code` is the same opaque id (FHIR-proper). Existing
 * facts keep their slug id until the (B) re-key migration.
 */
function mintFactId(): string {
  return doc(collection(db, "_observationDefinition")).id;
}
/**
 * Title-duplicate guard (case-insensitive). Replaces the old slug-collision
 * check — identity is now opaque, so we guard the human-facing title directly.
 */
function titleAlreadyExists(title: string): boolean {
  const t = title.trim().toLowerCase();
  return [...state.catalogueData.values()].some(
    (f) => ((f as { title?: string }).title || "").trim().toLowerCase() === t,
  );
}

/**
 * OMON-068: skeleton seed for a new custom goal's per-level carer-
 * guidance markdown. Five levels, each prefilled with `# title`,
 * subcategory subtitle, level preamble, and three default H2
 * sections (SETUP / EXECUTION / TROUBLESHOOTING). Jo edits from
 * this starting shape rather than an empty textarea, so the
 * authoring conventions in `reference/GUIDANCE-AUTHORING-CONVENTIONS.md`
 * (OMON-084) are taught by example on every new goal.
 */
function buildGuidanceSkeleton(
  goalTitle: string,
  subcategoryDisplay: string,
): Array<{ type: string; label: string; display: string }> {
  return LEVEL_NAMES.map((levelLabel, i) => {
    const levelN = i + 1;
    const next = i < 4 ? `Working towards: Level ${levelN + 1}` : "Maintaining independence";
    const display = [
      `# ${goalTitle}`,
      subcategoryDisplay,
      "",
      `Current level: ${levelN} - ${levelLabel} - ${next}`,
      "",
      "## SETUP",
      "",
      "(what the carer prepares before starting)",
      "",
      "## EXECUTION",
      "",
      "(how the carer guides the client through the task at this level)",
      "",
      "## TROUBLESHOOTING",
      "",
      "(what to do if things go wrong; when to escalate)",
    ].join("\n");
    return { type: "documentation", label: levelLabel, display };
  });
}

export async function createVariant(parentName: string, rawTitle: string): Promise<boolean> {
  const title = (rawTitle || "").trim();
  if (!title) return false; // blank → abandon, caller clears the draft

  const parent = state.catalogueData.get(parentName);
  if (!parent) {
    statusEl.textContent = "Parent fact not found — reload and try again.";
    return false;
  }

  if (!title.trim()) {
    statusEl.textContent = "Variant name must contain letters or numbers.";
    return false;
  }
  if (titleAlreadyExists(title)) {
    statusEl.textContent = `A catalogue entry titled "${title}" already exists.`;
    return false;
  }
  const name = mintFactId(); // OMON-192: opaque id, not slugify(title)

  // Inherit the parent's category verbatim (deep copy so we never alias the
  // parent's array). This carries goal-category + sub-category + every
  // trackable-capability coding with zero drift.
  const inheritedCategory = JSON.parse(JSON.stringify(parent.category || [])) as unknown[];
  const resolved = resolveCategoryCodes(parent.category);
  // ADR-0022 (catalogue unification): variant recording defaults derive from
  // the parent's CANONICAL signals — its `_recordingKind` and its
  // trackable-capability codings — not from the legacy `observation-category:
  // monitored-event` marker (which is being retired as a discriminator).
  const recordingKind = (parent as { _recordingKind?: string })._recordingKind || "attainment-5";
  const hasDevelopmental = trackableCapabilities(parent).includes("developmental");
  // Sub-category display for the guidance skeleton subtitle (best-effort).
  const subNode = resolved.goalCategory && resolved.goalSubcategory
    ? state.goalSubcategories.get(`${resolved.goalCategory}__${resolved.goalSubcategory}`)
    : undefined;
  const subcategoryDisplay = subNode?.display
    || resolved.goalSubcategory
    || state.goalCategories.get(resolved.goalCategory || "")?.display
    || "";

  const measurementType = (parent as { _measurementType?: string })._measurementType
    || "soma";

  try {
    const odRef = doc(db, "_observationDefinition", name);
    const odData: Record<string, unknown> = {
      resourceType: "ObservationDefinition",
      url: `stc:outcome/${name}`,
      name,
      title,
      status: "active",
      description: title,
      code: { coding: [{ system: "https://sweettreeconnect.com/fhir/CodeSystem/goal", code: name }] },
      category: inheritedCategory,
      permittedDataType: parent.permittedDataType
        ? [...parent.permittedDataType]
        : (recordingKind === "boolean" ? ["boolean"] : ["integer"]),
      qualifiedValue: [],
      active: true,
      // Trackable v3 two-tier: point back to the parent. The parent's hasMember
      // gets the forward ref below (in the same batch).
      _variantOf: { reference: `_observationDefinition/${parentName}` },
      _recordingKind: recordingKind,
      _measurementType: measurementType,
      meta: { security: metaSecurityFor(state.authClaims) },
    };
    // OMON-172: inherit the parent's librarian policy flags (variants follow the
    // parent, like category + capabilities). Only stamp fields the parent carries
    // so we don't write `false`/undefined noise onto every variant.
    const p = parent as { _targetable?: boolean; _escalatable?: boolean; _escalationThresholdDefault?: number; _mandatory?: boolean };
    if (p._targetable === true) odData._targetable = true;
    if (p._escalatable === true) {
      odData._escalatable = true;
      if (typeof p._escalationThresholdDefault === "number") odData._escalationThresholdDefault = p._escalationThresholdDefault;
    }
    if (p._mandatory === true) odData._mandatory = true;
    // ADR-0035 am.8: a variant inherits who owes it, and the lock.
    const pr = parent as { _shiftRolesDefault?: unknown; _rolesLocked?: boolean };
    if (Array.isArray(pr._shiftRolesDefault)) odData._shiftRolesDefault = [...pr._shiftRolesDefault];
    if (pr._rolesLocked === true) odData._rolesLocked = true;
    // OMON-174: inherit the parent's canonical monitored-event prompt (same
    // only-when-present rule as the policy flags above).
    const parentPrompt = (parent as { _monitoredEventPrompt?: string })._monitoredEventPrompt;
    if (parentPrompt) odData._monitoredEventPrompt = parentPrompt;
    // Zammad #49088: and its monitored-event carer guidance, by the same rule.
    const parentGuidance = (parent as { _monitoredEventGuidance?: string })._monitoredEventGuidance;
    if (parentGuidance) odData._monitoredEventGuidance = parentGuidance;

    const batch = writeBatch(db);
    batch.set(odRef, odData);

    let adData: Record<string, unknown> | null = null;
    // The 5-level carer-guidance ActivityDefinition belongs to the
    // developmental (1–5 attainment) regime. Author it when the parent carries
    // the developmental capability (ADR-0022: capability-driven, not marker-
    // driven). Pure monitored/safety facts don't get a support-level skeleton.
    if (hasDevelopmental) {
      const adRef = doc(db, "_activityDefinition", name);
      adData = {
        resourceType: "ActivityDefinition",
        url: `stc:activity/${name}`,
        name,
        title,
        status: "active",
        usage: `Over the next 8 weeks, the person will ${title.toLowerCase()} in at least 4 out of 5 opportunities with the least intrusive level of support.`,
        category: JSON.parse(JSON.stringify(inheritedCategory)),
        documentation: buildGuidanceSkeleton(title, subcategoryDisplay),
        active: true,
        meta: { security: metaSecurityFor(state.authClaims) },
      };
      batch.set(adRef, adData);
    }

    // Extend the parent's hasMember (forward edge) and ensure it's flagged a
    // parent. Build the new array from the cache mirror so we don't clobber
    // sibling variants. arrayUnion would also work, but an explicit array keeps
    // the cache mirror and the write identical.
    const parentRef = doc(db, "_observationDefinition", parentName);
    const existingMembers = Array.isArray(parent.hasMember) ? parent.hasMember : [];
    const newMembers = [...existingMembers, { reference: `_observationDefinition/${name}` }];
    batch.update(parentRef, { hasMember: newMembers, _trackableParent: true });

    await batch.commit();

    // Local-cache mirror: variant docs + parent edge, so the tree repaints with
    // the new variant nested under the parent without waiting for onSnapshot.
    state.catalogueData.set(name, odData as unknown as ObservationDefinition);
    if (adData) state.activityData.set(name, adData as unknown as ActivityDefinition);
    parent.hasMember = newMembers;
    (parent as { _trackableParent?: boolean })._trackableParent = true;

    statusEl.textContent = `Added variant "${title}"`;
    return true;
  } catch (err) {
    console.error("Failed to create variant:", parentName, name, err);
    statusEl.textContent = "Error creating variant — check console";
    return false;
  }
}

/**
 * 2026-06-11 (Ed): create a brand-new TOP-LEVEL fact directly under a goal
 * category — the tree-native replacement for the retired "+ Add Custom" form.
 * Seeds a bare Trackable: no sub-category, NO capability codings (the
 * librarian grants D/M/S in the inspector, which is also where recording
 * method and policy flags live), default attainment-5 recording. The AD
 * guidance skeleton is written alongside so the Attainment/Guidance tabs
 * have authorable content the moment D is granted.
 */
export async function createFact(categoryCode: string, rawTitle: string): Promise<boolean> {
  const title = (rawTitle || "").trim();
  if (!title) return false; // blank → abandon, caller clears the draft

  if (titleAlreadyExists(title)) {
    statusEl.textContent = `A catalogue entry titled "${title}" already exists.`;
    return false;
  }
  const name = mintFactId(); // OMON-192: opaque id, not slugify(title)
  const catDisplay = state.goalCategories.get(categoryCode)?.display || "";

  const category = [
    { coding: [{ system: "https://sweettreeconnect.com/fhir/observation-category", code: "outcome-assessment" }] },
    { coding: [{ system: "https://sweettreeconnect.com/fhir/CodeSystem/goal-category", code: categoryCode }] },
    // 2026-07-08 single-select: a trackable is exactly one of D/M/S. New facts
    // default to Developmental (the 264-strong majority); the librarian switches
    // to Monitored/Safety with one click in the inspector. (Previously new facts
    // started capability-less, which is no longer a valid state.)
    { coding: [{ system: "https://sweettreeconnect.com/fhir/CodeSystem/trackable-capability", code: "developmental", display: "Developmental" }] },
  ];

  try {
    const odRef = doc(db, "_observationDefinition", name);
    const odData: Record<string, unknown> = {
      resourceType: "ObservationDefinition",
      url: `stc:outcome/${name}`,
      name,
      title,
      status: "active",
      description: title,
      code: { coding: [{ system: "https://sweettreeconnect.com/fhir/CodeSystem/goal", code: name }] },
      category,
      permittedDataType: ["integer"],
      qualifiedValue: [],
      active: true,
      _measurementType: "soma",
      _recordingKind: "attainment-5",
      meta: { security: metaSecurityFor(state.authClaims) },
    };

    const adRef = doc(db, "_activityDefinition", name);
    const adData: Record<string, unknown> = {
      resourceType: "ActivityDefinition",
      url: `stc:activity/${name}`,
      name,
      title,
      status: "active",
      usage: `Over the next 8 weeks, the person will ${title.toLowerCase()} in at least 4 out of 5 opportunities with the least intrusive level of support.`,
      category: JSON.parse(JSON.stringify(category)),
      documentation: buildGuidanceSkeleton(title, catDisplay),
      active: true,
      meta: { security: metaSecurityFor(state.authClaims) },
    };

    const batch = writeBatch(db);
    batch.set(odRef, odData);
    batch.set(adRef, adData);
    await batch.commit();

    // Local-cache mirror so the tree repaints without waiting for onSnapshot.
    state.catalogueData.set(name, odData as unknown as ObservationDefinition);
    state.activityData.set(name, adData as unknown as ActivityDefinition);

    statusEl.textContent = `Added "${title}" to ${catDisplay || categoryCode}`;
    return true;
  } catch (err) {
    console.error("Failed to create fact:", categoryCode, name, err);
    statusEl.textContent = "Error creating fact — check console";
    return false;
  }
}
